import { createApiError } from '@/core/error-handling/ApiErrorHandler';
import type { MasterCrud, MasterPage, MasterQuery } from '@/features/masters';
import type {
  TemplateDetail,
  TemplateField,
  ProductSourcing,
  TemplateKind,
  TemplateOrgUnit,
  TemplatePayload,
  TemplateRecord,
  TemplateSection,
} from '../models/template.types';
import {
  NEW_TEMPLATE_PRODUCT_SOURCING,
  TEMPLATE_CODE_PREFIX,
  TEMPLATE_STATUS_LABEL,
  templateCategoryIdsOf,
} from '../models/template.types';
import {
  TEMPLATE_DESCRIPTION_MAX,
  TEMPLATE_DESCRIPTION_MAX_MESSAGE,
  TEMPLATE_NAME_PATTERN,
  TEMPLATE_NAME_PATTERN_MESSAGE,
} from '../../builder/models/templateFieldTitle';
import type { HierarchyScope } from '../models/hierarchyScope';
import { EMPTY_SCOPE, scopeApplies, scopeMatches, scopeOfQuery } from '../models/hierarchyScope';
import { MOCK_TEMPLATE_CATEGORIES, mockDescribeScope } from './templateHierarchy.mock';

/**
 * The reference's own rows, behind `env.ENABLE_MOCKS`.
 *
 * `NiraxoBid_Template.html` runs entirely on these -- nine RFQ, seven purchase
 * request and six supplier onboarding rows, with no request anywhere. They live
 * here, and only here: the live path is the API. A screen that fell back to
 * fixtures when a request failed would look like it was working.
 */

interface MockRow {
  name: string;
  templateCategory: string;
  fieldCount: number;
  modified: string;
  createdBy: number;
  status: string;
}

/**
 * The names the fixture ids resolve to.
 *
 * The real API resolves these through `UserDirectory`; the mock has to carry the
 * same shape or the Created By column would be the one thing that behaved
 * differently under mocks than against the server.
 */
/** "Vishal Patil" -> "vp", so the mock matches an initials search as the server does. */
function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toLowerCase();
}

const MOCK_CREATORS: Readonly<Record<number, string>> = {
  1: 'Ashok Kumar',
  2: 'Riya Anand',
  3: 'Neha Patil',
  4: 'Karan Mehta',
  5: 'Priya Sharma',
  6: 'Vishal Patil',
};

const RFQ_ROWS: MockRow[] = [
  { name: 'Standard Goods RFQ', templateCategory: 'Goods', fieldCount: 12, modified: '2026-09-12T00:00:00', createdBy: 1, status: 'Active' },
  { name: 'Capex Equipment Bid Form', templateCategory: 'Works', fieldCount: 12, modified: '2026-09-09T00:00:00', createdBy: 2, status: 'Active' },
  { name: 'Software Licence RFQ', templateCategory: 'IT & Software', fieldCount: 12, modified: '2026-09-04T00:00:00', createdBy: 3, status: 'Active' },
  { name: 'Facility Services RFQ', templateCategory: 'Services', fieldCount: 12, modified: '2026-08-28T00:00:00', createdBy: 4, status: 'Inactive' },
  { name: 'Raw Material Reverse Auction', templateCategory: 'Goods', fieldCount: 12, modified: '2026-08-21T00:00:00', createdBy: 1, status: 'Active' },
  { name: 'Civil Works Tender Form', templateCategory: 'Works', fieldCount: 12, modified: '2026-08-14T00:00:00', createdBy: 2, status: 'Inactive' },
  { name: 'Cloud Infrastructure RFQ', templateCategory: 'IT & Software', fieldCount: 12, modified: '2026-08-02T00:00:00', createdBy: 3, status: 'Active' },
  { name: 'Manpower Supply RFQ', templateCategory: 'Services', fieldCount: 12, modified: '2026-07-27T00:00:00', createdBy: 4, status: 'Active' },
  { name: 'Packaging Materials RFQ', templateCategory: 'Goods', fieldCount: 12, modified: '2026-09-13T00:00:00', createdBy: 1, status: 'Draft' },
];

const PR_ROWS: MockRow[] = [
  { name: 'Standard Purchase Request', templateCategory: 'Goods', fieldCount: 10, modified: '2026-08-05T00:00:00', createdBy: 2, status: 'Active' },
  { name: 'IT Equipment PR', templateCategory: 'IT & Software', fieldCount: 9, modified: '2026-07-27T00:00:00', createdBy: 5, status: 'Active' },
  { name: 'Facilities Supplies PR', templateCategory: 'Goods', fieldCount: 8, modified: '2026-07-15T00:00:00', createdBy: 2, status: 'Active' },
  { name: 'Marketing Materials PR', templateCategory: 'Services', fieldCount: 9, modified: '2026-06-20T00:00:00', createdBy: 6, status: 'Inactive' },
  { name: 'Bulk Raw Material PR', templateCategory: 'Goods', fieldCount: 11, modified: '2026-08-02T00:00:00', createdBy: 5, status: 'Active' },
  { name: 'Urgent Consumables PR', templateCategory: 'Goods', fieldCount: 8, modified: '2026-05-30T00:00:00', createdBy: 2, status: 'Inactive' },
  { name: 'Capex Equipment PR', templateCategory: 'Works', fieldCount: 12, modified: '2026-09-11T00:00:00', createdBy: 1, status: 'Draft' },
];

const SO_ROWS: MockRow[] = [
  { name: 'Domestic Supplier Onboarding', templateCategory: 'Goods', fieldCount: 12, modified: '2026-08-08T00:00:00', createdBy: 2, status: 'Active' },
  { name: 'International Supplier Onboarding', templateCategory: 'Goods', fieldCount: 15, modified: '2026-07-30T00:00:00', createdBy: 6, status: 'Active' },
  { name: 'Service Provider Onboarding', templateCategory: 'Services', fieldCount: 11, modified: '2026-07-12T00:00:00', createdBy: 5, status: 'Active' },
  { name: 'MSME Supplier Onboarding', templateCategory: 'Goods', fieldCount: 10, modified: '2026-06-18T00:00:00', createdBy: 2, status: 'Inactive' },
  { name: 'Contractor Onboarding', templateCategory: 'Works', fieldCount: 13, modified: '2026-08-01T00:00:00', createdBy: 5, status: 'Active' },
  { name: 'Logistics Partner Onboarding', templateCategory: 'Services', fieldCount: 12, modified: '2026-09-10T00:00:00', createdBy: 1, status: 'Draft' },
];

const STATUS_ID: Readonly<Record<string, number>> = {
  Active: 11,
  Draft: 12,
  Inactive: 13,
  Deleted: 17,
};

function rowsFor(kind: TemplateKind): MockRow[] {
  if (kind === 'RFQ') return RFQ_ROWS;
  if (kind === 'PURCHASE_REQUEST') return PR_ROWS;
  return SO_ROWS;
}

/** One stored field, with the defaults every fixture shares. */
function field(
  fieldKey: string,
  label: string,
  fieldType: TemplateField['fieldType'],
  mandatory: boolean,
  extra: Partial<TemplateField> = {},
): TemplateField {
  return {
    fieldKey,
    label,
    fieldType,
    helperText: null,
    system: false,
    mandatory,
    conditional: false,
    fieldGroup: null,
    statusId: 11,
    statusName: 'Active',
    ...extra,
  };
}

const INFORMATION: TemplateSection = { sectionKey: 'template-information', title: 'Template Information', ordinal: 0, fields: [] };

/**
 * The trees behind the fixtures a runtime form is exercised against, by name.
 *
 * Every other row still answers with no sections, as it always has: these exist
 * so a mocked Purchase Request or Supplier Onboarding form draws a real form, and so the tests
 * can prove two templates draw two different forms.
 */
const MOCK_SECTIONS: Readonly<Record<string, TemplateSection[]>> = {
  'Standard Purchase Request': [
    INFORMATION,
    {
      sectionKey: 'pr-details',
      title: 'PR Details',
      ordinal: 1,
      fields: [
        /* The PR Title the builder declares (`pr-title`): what the list's PR Title column reads (2026-10-09). */
        field('pr-title', 'PR Title', 'Short text', false, { system: true, ordinal: -1 }),
        field('request-name', 'Request Name', 'Short text', true, { system: true, ordinal: 0 }),
        field('request-date', 'Request Date', 'Date', true, { system: true, ordinal: 1 }),
        field('delivery-date', 'Delivery Date', 'Date', true, { system: true, ordinal: 2 }),
        field('supplier', 'Supplier', 'Dropdown', false, { ordinal: 3, options: ['Acme Supplies', 'Globex Traders'] }),
        /* Description between the other fields: the request still draws it, with Remark, on the last row. */
        field('description', 'Description', 'Long text', false, { ordinal: 4 }),
        field('quantity', 'Quantity', 'Number', true, { ordinal: 5, helperText: 'Default: 1' }),
        field('remark', 'Remark', 'Long text', false, { ordinal: 6 }),
      ],
    },
    {
      /* The line columns, named as an author may rename them, and a field added beside them. */
      sectionKey: 'line-items',
      title: 'Line items',
      ordinal: 2,
      fields: [
        field('location', 'Delivery Location', 'Dropdown', true, { system: true, ordinal: 0, helperText: 'Select Location' }),
        field('category', 'Category', 'Dropdown', true, { system: true, ordinal: 1, helperText: 'Select Category' }),
        field('product', 'Product', 'Dropdown', true, { system: true, ordinal: 2, helperText: 'Select product' }),
        field('quantity', 'Quantity', 'Number', true, { system: true, ordinal: 3, helperText: 'Default: 1' }),
        field('custom-0-cost-centre', 'Cost Centre', 'Short text', false, { ordinal: 4 }),
      ],
    },
    {
      sectionKey: 'custom-fields',
      title: 'Custom fields',
      ordinal: 3,
      /* One of each kind a user may add, so the form shows each as its own control. */
      fields: [
        field('custom-0-budget-code', 'Budget Code', 'Short text', false, { ordinal: 0 }),
        field('custom-1-quotation', 'Quotation', 'File upload', false, { ordinal: 1 }),
        field('custom-2-urgent', 'Urgent', 'Checkbox', false, { ordinal: 2 }),
        field('custom-3-needed-by', 'Needed By', 'Date', false, { ordinal: 3 }),
        field('custom-4-vendor', 'Preferred Vendor', 'Dropdown', false, { ordinal: 4, options: ['Acme', 'Globex'] }),
      ],
    },
  ],
  /*
    One step, so the onboarding wizard has a Step 1 to draw the Organization Hierarchy on (2026-10-14) -- the
    template mapped to its entity alone.
  */
  'International Supplier Onboarding': [
    INFORMATION,
    {
      sectionKey: 'legal-and-company-details',
      title: 'Legal & Company Details',
      ordinal: 1,
      fields: [
        field('legal-business-name', 'Legal Business Name', 'Text', true, { system: true, ordinal: 0, helperText: 'Enter registered legal name' }),
      ],
    },
  ],
  /* A Supplier Onboarding tree, so the View dialog's section tabs have something to show (2026-10-08). */
  'Domestic Supplier Onboarding': [
    INFORMATION,
    {
      sectionKey: 'legal-and-company-details',
      title: 'Legal & Company Details',
      ordinal: 1,
      fields: [
        field('legal-business-name', 'Legal Business Name', 'Text', true, { system: true, ordinal: 0, helperText: 'Enter registered legal name' }),
        field('trade-name', 'Trade / DBA Name', 'Text', false, { system: true, ordinal: 1, helperText: 'Optional' }),
        field('gst-certificate', 'GST Certificate', 'File upload', false, { ordinal: 2 }),
      ],
    },
    {
      sectionKey: 'category-and-capability',
      title: 'Category & Capability',
      ordinal: 2,
      fields: [
        field('commodity-interest', 'Category / Commodity Interest', 'Multi-select', true, { system: true, ordinal: 0, options: ['Goods', 'Services'] }),
        field('annual-turnover', 'Annual Turnover', 'Currency', false, { system: true, ordinal: 1, helperText: 'In payment currency' }),
        field('service-locations', 'Manufacturing / Service Locations', 'Multi-select Dropdown', true, { system: true, ordinal: 2 }),
        /* Optional, so a supplier saved without products still passes; a refusal under it has a field (NB-645). */
        field('products', 'Products', 'Multi-select Dropdown', false, { system: true, ordinal: 2, helperText: 'From Product master' }),
        field('legacy-capacity', 'Legacy Capacity', 'Number', false, { ordinal: 3, statusId: 13, statusName: 'Inactive' }),
      ],
    },
  ],
  'Standard Goods RFQ': [
    INFORMATION,
    {
      sectionKey: 'rfq-details',
      title: 'RFQ Details',
      ordinal: 1,
      fields: [
        field('title', 'RFQ Title', 'Short text', true, { system: true }),
        field('description', 'Description', 'Long text', false, { system: true }),
      ],
    },
    {
      sectionKey: 'add-product-and-pr',
      title: 'Add Product & PR',
      ordinal: 2,
      fields: [field('description', 'Line Description', 'Long text', false)],
    },
  ],
};

let nextId = 1;
const store = new Map<number, TemplateRecord>();
/** The templates' hierarchy mappings (PR Templates, Supplier Onboarding), by template id -- what the server keeps in os_form_template_scope. */
const scopes = new Map<number, HierarchyScope>();
/** The BID templates' Product Sourcing (2026-10-21), by template id. A seeded one has none, as a pre-existing row. */
const productSourcings = new Map<number, ProductSourcing>();
/** The BID templates' "Allow suppliers to withdraw" (2026-10-22), by template id. A seeded one has none and reads true. */
const allowWithdraws = new Map<number, boolean>();

/*
  The seeded templates mapped to the hierarchy, so its filters have something to narrow; the rest are unmapped and
  so apply everywhere.
*/
const MOCK_SCOPES: Readonly<Record<string, HierarchyScope>> = {
  /* PR Templates (NB-355): one under Procurement → Direct Materials, one under Sales → Accounts. */
  'Standard Purchase Request': { legalEntityIds: [1], businessUnitIds: [11], divisionIds: [111], departmentIds: [1111] },
  'IT Equipment PR': { legalEntityIds: [1], businessUnitIds: [12], divisionIds: [121], departmentIds: [] },
  /*
    Supplier Onboarding (2026-10-09): one mapped to Procurement → Direct Materials → Purchase Team; the others are
    mapped to the entity alone, so a supplier on them is placed anywhere in it.
  */
  'Domestic Supplier Onboarding': { legalEntityIds: [1], businessUnitIds: [11], divisionIds: [111], departmentIds: [1111] },
};

/** The kinds mapped to the hierarchy -- the server's `TemplateKind.mapsHierarchy`. */
function mapsHierarchy(kind: TemplateKind): boolean {
  return kind === 'PURCHASE_REQUEST' || kind === 'SUPPLIER_ONBOARDING';
}

/** How many forms the mocks submitted from each template -- the cards' "Used N times". */
const uses = new Map<number, number>();

/** Counts one submission from a template; the forms mock calls it on every accepted submit. */
export function mockRecordTemplateUse(templateId: number): void {
  uses.set(templateId, (uses.get(templateId) ?? 0) + 1);
}

/** Forgets every count, with the forms mock's own reset -- the requests they counted are gone with it. */
export function mockResetTemplateUses(): void {
  uses.clear();
}

/** A template's stored mapping, for the Purchase Request and Supplier Onboarding mocks. */
export function mockTemplateScope(templateId: number): HierarchyScope {
  seed();
  return scopes.get(templateId) ?? EMPTY_SCOPE;
}

/**
 * Who the Purchase Requests mocks answer for (2026-10-09): an Admin by default; a test (or a developer) sets an
 * Initiator with the Business Unit, Division and Department their user record places them in.
 */
export interface MockPurchaseRequestCaller {
  admin: boolean;
  businessUnitId: number | null;
  divisionId: number | null;
  departmentId: number | null;
}

const ADMIN_CALLER: MockPurchaseRequestCaller = { admin: true, businessUnitId: null, divisionId: null, departmentId: null };
let prCaller: MockPurchaseRequestCaller = ADMIN_CALLER;

/** Sets the caller; null puts the Admin back. */
export function setMockPurchaseRequestCaller(next: MockPurchaseRequestCaller | null): void {
  prCaller = next ?? ADMIN_CALLER;
}

export function mockPurchaseRequestCaller(): MockPurchaseRequestCaller {
  return prCaller;
}

/**
 * The server's Initiator rule: a PR Template that maps at least one Division and, at Business Unit and Division,
 * includes the caller's own value -- matched down to the Division only (NB-699, 2026-11-02), so a template mapped to
 * another Department of the caller's Division is offered too. An Admin is not restricted by placement.
 */
export function mockOfferedToCaller(templateId: number): boolean {
  if (prCaller.admin) return true;
  const mapped = scopes.get(templateId) ?? EMPTY_SCOPE;
  if (mapped.divisionIds.length === 0 || prCaller.divisionId === null) return false;
  const at = (ids: readonly number[], own: number | null) => ids.length === 0 || (own !== null && ids.includes(own));
  return at(mapped.businessUnitIds, prCaller.businessUnitId) && at(mapped.divisionIds, prCaller.divisionId);
}

/** As `GET /purchase-requests/templates/{id}`: an Active PR Template the caller is offered, with its mapping. */
export async function mockPurchaseRequestTemplate(id: number): Promise<TemplateDetail> {
  seed();
  const record = store.get(id);
  if (!record || record.kind !== 'PURCHASE_REQUEST' || record.statusName !== 'Active' || !mockOfferedToCaller(id)) {
    throw createApiError({ message: 'Template not found', code: 'NOT_FOUND_ERROR', status: 404 });
  }
  return Promise.resolve(structuredClone(detailOf(record)));
}

function appliesTo(row: TemplateRecord, query: MasterQuery): boolean {
  return scopeApplies(scopes.get(row.id) ?? EMPTY_SCOPE, scopeOfQuery(query));
}

/**
 * As the server: the kind's prefix and the next number of that kind -- BIDT-001, PRT-001, SOT-001 -- three digits,
 * assigned on create and never changed. The server counts within each entity; the mock holds one, so it counts per kind.
 */
function mockTemplateCode(kind: TemplateKind): string {
  const prefix = `${TEMPLATE_CODE_PREFIX[kind]}-`;
  let highest = 0;
  store.forEach((row) => {
    if (row.kind === kind && row.templateCode?.startsWith(prefix)) {
      highest = Math.max(highest, Number(row.templateCode.slice(prefix.length)) || 0);
    }
  });
  return `${prefix}${String(highest + 1).padStart(3, '0')}`;
}

function seed(): void {
  if (store.size > 0) return;
  (['RFQ', 'PURCHASE_REQUEST', 'SUPPLIER_ONBOARDING'] as const).forEach((kind) => {
    rowsFor(kind).forEach((row) => {
      const id = nextId++;
      store.set(id, {
        id,
        kind,
        name: row.name,
        templateCode: mockTemplateCode(kind),
        description: null,
        templateCategory: row.templateCategory || null,
        fieldCount: row.fieldCount,
        statusId: STATUS_ID[row.status] ?? 11,
        statusName: row.status,
        createdBy: row.createdBy,
        createdByName: MOCK_CREATORS[row.createdBy] ?? null,
        createdDate: row.modified,
        modifiedBy: row.createdBy,
        modifiedDate: row.modified,
      });
      const mapped = mapsHierarchy(kind) ? MOCK_SCOPES[row.name] : undefined;
      if (mapped) scopes.set(id, mapped);
      /* A PR Template's Category master rows, and their names in place of the fixture's. */
      if (kind === 'PURCHASE_REQUEST') {
        const record = store.get(id) as TemplateRecord;
        store.set(id, { ...record, ...categoriesOf(MOCK_PR_CATEGORIES[row.name] ?? []) });
      }
    });
  });
}

/*
  The PR Templates' Category master rows (templateHierarchy.mock's MOCK_TEMPLATE_CATEGORIES). IT Equipment PR names
  both, so a card shows two tags and either category's filter keeps it; the rest name one.
*/
const MOCK_PR_CATEGORIES: Readonly<Record<string, readonly number[]>> = {
  'Standard Purchase Request': [601],
  'IT Equipment PR': [601, 602],
  'Facilities Supplies PR': [602],
  'Bulk Raw Material PR': [602],
  'Urgent Consumables PR': [602],
};

/** A PR Template's categories, as the server writes them on a row: ids, `{ id, name }`s, and the names joined. */
function categoriesOf(ids: readonly number[]): Pick<TemplateRecord, 'templateCategory' | 'categoryIds' | 'categories'> {
  const found = ids
    .map((id) => MOCK_TEMPLATE_CATEGORIES.find((category) => category.id === id))
    .filter((category): category is (typeof MOCK_TEMPLATE_CATEGORIES)[number] => category !== undefined);
  return {
    categoryIds: found.map((category) => category.id),
    categories: found.map((category) => ({ id: category.id, name: category.name })),
    templateCategory: found.length > 0 ? found.map((category) => category.name).join(', ') : null,
  };
}

/**
 * A saved template's category: the RFQ kind's free text; a PR Template's master rows named as the server names them;
 * Supplier Onboarding's master rows (several, the known ones kept).
 */
function categoryOf(payload: TemplatePayload): Pick<TemplateRecord, 'templateCategory' | 'categoryIds' | 'categories'> {
  if (payload.kind === 'SUPPLIER_ONBOARDING') {
    const ids = (payload.categoryIds ?? []).filter((id) => MOCK_TEMPLATE_CATEGORIES.some((category) => category.id === id));
    return { templateCategory: null, categoryIds: ids };
  }
  if (payload.kind !== 'PURCHASE_REQUEST') return { templateCategory: payload.templateCategory };
  return categoriesOf(payload.categoryIds ?? []);
}

/** A PR Template's Category master rows -- what its request's line Category dropdown offers (2026-10-09). */
export function mockTemplateCategoryIds(templateId: number): number[] {
  seed();
  const record = store.get(templateId);
  return record ? templateCategoryIdsOf(record) : [];
}

function detailOf(record: TemplateRecord): TemplateDetail {
  return {
    ...record,
    sections: MOCK_SECTIONS[record.name] ?? [],
    ...(mapsHierarchy(record.kind) ? { scope: mockDescribeScope(scopes.get(record.id) ?? EMPTY_SCOPE) } : {}),
    allowMixedSourcing: productSourcings.get(record.id) === 'MIXED',
    ...(productSourcings.has(record.id) ? { productSourcing: productSourcings.get(record.id) } : {}),
    ...(record.kind === 'RFQ' ? { allowSupplierWithdraw: allowWithdraws.get(record.id) ?? true } : {}),
  };
}

/**
 * Records Product Sourcing and "Allow suppliers to withdraw" as the server does: only the BID kind keeps them; a
 * create without them is With Purchase Request (2026-10-22) and true, an update without them keeps the stored ones.
 */
function recordProductSourcing(id: number, payload: TemplatePayload, creating: boolean) {
  if (payload.kind !== 'RFQ') productSourcings.delete(id);
  else if (payload.productSourcing) productSourcings.set(id, payload.productSourcing);
  else if (creating) productSourcings.set(id, NEW_TEMPLATE_PRODUCT_SOURCING);
  if (payload.kind !== 'RFQ') allowWithdraws.delete(id);
  else if (payload.allowSupplierWithdraw !== undefined) allowWithdraws.set(id, payload.allowSupplierWithdraw);
  else if (creating) allowWithdraws.set(id, true);
}

/**
 * The active listing behind New Purchase Request's cards, over the same store.
 *
 * Active templates of the kind mapped to the selected hierarchy -- the rule the
 * server applies. Sharing the store is what lets a card opened from this
 * listing load its detail from the mock above.
 */
export async function mockActiveTemplateListing(
  query: MasterQuery,
  /* Purchase Requests' cards are PR Templates. */
  kind: TemplateKind = 'PURCHASE_REQUEST',
): Promise<MasterPage<TemplateRecord>> {
  seed();
  const term = query.search?.trim().toLowerCase() ?? '';
  const matched = [...store.values()]
    .filter((row) => row.kind === kind && row.statusName === 'Active')
    .filter((row) => scopeMatches(scopes.get(row.id) ?? EMPTY_SCOPE, scopeOfQuery(query)))
    /* An Initiator is offered only the PR Templates mapped to their own hierarchy (2026-10-09). */
    .filter((row) => kind !== 'PURCHASE_REQUEST' || mockOfferedToCaller(row.id))
    /* New Purchase Request's category filter: the templates of those Category master rows. */
    .filter((row) => {
      const wanted = String((query as { categoryIds?: unknown }).categoryIds ?? '')
        .split(',')
        .filter((id) => id !== '')
        .map(Number);
      /* A template naming any of the chosen categories. */
      return wanted.length === 0 || templateCategoryIdsOf(row).some((id) => wanted.includes(id));
    })
    .filter(
      (row) =>
        term === '' ||
        row.name.toLowerCase().includes(term) ||
        (row.createdByName ?? '').toLowerCase().includes(term),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const start = query.page * query.size;
  /* The cards' two counts, as the server adds them: the sections holding fields, and the uses. */
  const rows = matched.slice(start, start + query.size).map((row) => ({
    ...row,
    sectionCount: (MOCK_SECTIONS[row.name] ?? []).filter((section) => section.fields.length > 0).length,
    usedCount: uses.get(row.id) ?? 0,
  }));
  return { rows, total: matched.length };
}

/** The builder's lookups in mock mode: the Category master's names and a small entity hierarchy. */
const MOCK_CATEGORIES = ['Electronics', 'Goods', 'Raw Material', 'Services'];
const MOCK_UNITS: readonly TemplateOrgUnit[] = [
  { id: 1, name: 'Corporate' },
  { id: 2, name: 'Manufacturing' },
];
const MOCK_DIVISIONS: Readonly<Record<number, readonly TemplateOrgUnit[]>> = {
  1: [{ id: 11, name: 'Finance' }],
  2: [
    { id: 21, name: 'Production' },
    { id: 22, name: 'Quality' },
  ],
};
const MOCK_DEPARTMENTS: Readonly<Record<number, readonly TemplateOrgUnit[]>> = {
  11: [{ id: 111, name: 'Accounts' }],
  21: [{ id: 211, name: 'Assembly' }],
  22: [{ id: 221, name: 'Inspection' }],
};

/** A `MasterCrud` over the fixtures, plus the calls the screen makes directly. */
export function mockTemplateApi(kind: TemplateKind): MasterCrud<TemplateRecord, TemplatePayload> & {
  detail: (id: number) => Promise<TemplateDetail>;
  setStatus: (id: number, status: string) => Promise<TemplateDetail>;
  categories: () => Promise<string[]>;
  businessUnits: () => Promise<TemplateOrgUnit[]>;
  divisions: (businessUnitId: number) => Promise<TemplateOrgUnit[]>;
  departments: (divisionId: number) => Promise<TemplateOrgUnit[]>;
} {
  seed();

  const visible = (): TemplateRecord[] =>
    [...store.values()].filter((row) => row.kind === kind && row.statusName !== 'Deleted');

  /*
    The server's two refusals of a name, keyed `name` (2026-10-09): letters, digits, spaces and "&" only
    (`SaveTemplateRequest.name`, 400), and not one a live template of the same kind already uses (409).
  */
  /*
    The server's limit on the description, keyed `description` (`SaveTemplateRequest`, `@Size(max = 500)`).

    Mirrored here for the same reason the name's two refusals are: mock mode is how the screens are driven
    without a backend, and a rule the real server enforces but the mock does not is a rule nobody sees broken
    until it reaches an environment that has one.
  */
  const refuseDescription = (payload: TemplatePayload) => {
    const description = payload.description?.trim() ?? '';
    if (description.length > TEMPLATE_DESCRIPTION_MAX) {
      throw createApiError({
        message: 'Request validation failed',
        code: 'VALIDATION_ERROR',
        status: 400,
        details: { description: [TEMPLATE_DESCRIPTION_MAX_MESSAGE] },
      });
    }
  };

  const refuseName = (payload: TemplatePayload, ownId: number | null) => {
    refuseDescription(payload);
    const name = payload.name.trim();
    if (!TEMPLATE_NAME_PATTERN.test(name)) {
      throw createApiError({
        message: 'Request validation failed',
        code: 'VALIDATION_ERROR',
        status: 400,
        details: { name: [TEMPLATE_NAME_PATTERN_MESSAGE] },
      });
    }
    const taken = [...store.values()].some(
      (row) =>
        row.id !== ownId
        && row.kind === payload.kind
        && row.statusName !== 'Deleted'
        && row.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (taken) {
      throw createApiError({
        message: 'A template with this name already exists.',
        code: 'UNKNOWN_ERROR',
        status: 409,
        details: { name: ['A template with this name already exists.'] },
      });
    }
  };

  return {
    list: async (query: MasterQuery): Promise<MasterPage<TemplateRecord>> => {
      const term = query.search?.trim().toLowerCase() ?? '';
      const status = query.status?.trim() ?? '';
      /* The filter builder's conditions, honoured the way the server honours
         them: a case-insensitive contains per named property, all of which must
         hold. Without this a filter applied in mock mode would change nothing,
         which is indistinguishable from a filter that does not work. */
      const conditions = query.filters ?? [];
      const matches = (row: TemplateRecord): boolean =>
        conditions.every((condition) => {
          /*
            A date row is a range, not a substring -- the same rule the server
            applies. The value is a day and the column is a timestamp, so "is"
            means that whole day, "is after" means from the next day's midnight,
            and "is before" means up to this day's. Comparing the ISO strings
            does that correctly without parsing either side into an instant,
            which is also how the off-by-one-day error is avoided.
          */
          if (condition.column === 'modifiedDate') {
            const day = condition.value.trim();
            if (day === '') return true;

            const modified = (row.modifiedDate ?? '').slice(0, 10);
            if (modified === '') return false;

            /* Normalised as the server normalises it, so "isAfter", "is_after"
               and "Is after" are one comparison rather than three. */
            const how = condition.comparison.trim().replace(/[\s_]/g, '').toLowerCase();
            if (how === 'isafter' || how === 'after') return modified > day;
            if (how === 'isbefore' || how === 'before') return modified < day;
            return modified === day;
          }

          const values: Record<string, string> = {
            name: row.name,
            templateCode: row.templateCode ?? '',
            templateCategory: row.templateCategory ?? '',
            fieldCount: String(row.fieldCount),
            /* The NAME, as the server resolves it -- the column shows a person, so the filter row is typed with one. */
            createdBy: row.createdByName ?? '',
            modifiedDate: row.modifiedDate ?? '',
          };
          const raw = values[condition.column] ?? '';
          return raw.toLowerCase().includes(condition.value.trim().toLowerCase());
        });

      /*
        The same five columns the server searches, and matched the same way: the
        two text columns and the creator by substring, the field count by
        equality, the status by the label's prefix, and the date by day or month.
        A mock that searched fewer columns would make the box behave one way here
        and another against the server, which is the one place nobody checks.
      */
      const searched = (row: TemplateRecord): boolean => {
        if (term === '') return true;

        const label = TEMPLATE_STATUS_LABEL[row.statusName ?? ''] ?? row.statusName ?? '';
        return (
          row.name.toLowerCase().includes(term) ||
          (row.templateCode ?? '').toLowerCase().includes(term) ||
          (row.templateCategory ?? '').toLowerCase().includes(term) ||
          (row.createdByName ?? '').toLowerCase().includes(term) ||
          initialsOf(row.createdByName) === term ||
          label.toLowerCase().startsWith(term) ||
          String(row.fieldCount) === term ||
          (row.modifiedDate ?? '').startsWith(term)
        );
      };

      const matched = visible().filter(
        (row) =>
          searched(row) && (status === '' || row.statusName === status) && matches(row) && appliesTo(row, query),
      );
      const start = query.page * query.size;
      return { rows: matched.slice(start, start + query.size), total: matched.length };
    },

    get: async (id) => {
      const found = store.get(id);
      if (!found) throw new Error(`No mock template ${String(id)}`);
      return found;
    },

    create: async (payload) => {
      refuseName(payload, null);
      const id = nextId++;
      const record: TemplateRecord = {
        id,
        kind: payload.kind,
        name: payload.name,
        templateCode: mockTemplateCode(payload.kind),
        description: payload.description,
        ...categoryOf(payload),
        fieldCount: payload.sections.reduce((sum, section) => sum + section.fields.length, 0),
        statusId: STATUS_ID[payload.statusName] ?? 11,
        statusName: payload.statusName,
        /* Carried, not dropped: this is what lets a draft remember that its
           author had the Status toggle on. */
        activeIntent: payload.activeIntent,
        createdBy: 1,
        createdDate: new Date().toISOString(),
        modifiedBy: 1,
        modifiedDate: new Date().toISOString(),
      };
      store.set(id, record);
      if (payload.scope) scopes.set(id, payload.scope);
      recordProductSourcing(id, payload, true);
      return record;
    },

    update: async (id, payload) => {
      const existing = store.get(id);
      if (!existing) throw new Error(`No mock template ${String(id)}`);
      refuseName(payload, id);
      const updated: TemplateRecord = {
        ...existing,
        name: payload.name,
        description: payload.description,
        ...categoryOf(payload),
        fieldCount: payload.sections.reduce((sum, section) => sum + section.fields.length, 0),
        templateCode: existing.templateCode,
        statusId: STATUS_ID[payload.statusName] ?? 11,
        statusName: payload.statusName,
        activeIntent: payload.activeIntent,
        modifiedDate: new Date().toISOString(),
      };
      store.set(id, updated);
      /* Absent leaves the stored mapping alone, as the server does. */
      if (payload.scope) scopes.set(id, payload.scope);
      recordProductSourcing(id, payload, false);
      return updated;
    },

    remove: async (id) => {
      const existing = store.get(id);
      if (existing) store.set(id, { ...existing, statusId: 17, statusName: 'Deleted' });
    },

    categories: () => Promise.resolve([...MOCK_CATEGORIES]),
    businessUnits: () => Promise.resolve([...MOCK_UNITS]),
    divisions: (businessUnitId: number) => Promise.resolve([...(MOCK_DIVISIONS[businessUnitId] ?? [])]),
    departments: (divisionId: number) => Promise.resolve([...(MOCK_DEPARTMENTS[divisionId] ?? [])]),
    detail: async (id) => {
      const found = store.get(id);
      if (!found) throw new Error(`No mock template ${String(id)}`);
      return detailOf(found);
    },

    setStatus: async (id, status) => {
      const existing = store.get(id);
      if (!existing) throw new Error(`No mock template ${String(id)}`);
      const updated = { ...existing, statusId: STATUS_ID[status] ?? 11, statusName: status };
      store.set(id, updated);
      return detailOf(updated);
    },
  };
}
